/* eslint-disable react-hooks/exhaustive-deps */
/**
 * RideAnalytics.jsx
 *
 * Ride Analytics Dashboard (Subscription-based):
 * 1. Overview - Daily, Weekly, Monthly ride counts
 * 2. Monthly - Daily breakdown for selected month
 * 3. Cities - City-wise ride breakdown
 * 4. Drivers - Driver-wise ride stats
 * 5. Map View - Google Maps with pickup/drop locations
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { toast } from "react-toastify";
import {
  GoogleMap,
  LoadScript,
  Marker,
  InfoWindow,
  Polyline,
} from "@react-google-maps/api";
import StatCard from "../components/StatCard";
import Badges from "../components/Badges";
import AccessControl from "../components/AccessControl";
import {
  getRideOverview,
  getWeeklyChartData,
  getMonthlyRideStats,
  getCityWiseRides,
  getDriverWiseRides,
  getPeakHoursAnalysis,
  getRidesForMap,
} from "../api/rideAnalyticsApi";
import Loader from "../components/Loader";

// ✅ Colors
const COLORS = {
  primary: "#6366f1",
  success: "#22c55e",
  warning: "#f59e0b",
  danger: "#ef4444",
  info: "#3b82f6",
  purple: "#8b5cf6",
};

const PIE_COLORS = [
  "#22c55e",
  "#ef4444",
  "#f59e0b",
  "#3b82f6",
  "#8b5cf6",
  "#ec4899",
];

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const mapContainerStyle = {
  width: "100%",
  height: "calc(100vh - 220px)",
  borderRadius: 12,
};

const defaultCenter = {
  lat: 11.0168,
  lng: 76.9558,
};

// ✅ Status colors for map
const getStatusColor = (status) => {
  switch (status) {
    case "trip_completed":
      return "#22c55e";
    case "cancelled":
      return "#ef4444";
    case "accepted":
    case "confirmed":
      return "#f59e0b";
    case "requested":
      return "#3b82f6";
    default:
      return "#6b7280";
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case "trip_completed":
      return "Completed";
    case "cancelled":
      return "Cancelled";
    case "accepted":
      return "Accepted";
    case "confirmed":
      return "Confirmed";
    case "requested":
      return "Requested";
    default:
      return status;
  }
};

// ✅ Polyline colors for routes
const ROUTE_COLORS = [
  "#6366f1",
  "#ec4899",
  "#14b8a6",
  "#f97316",
  "#8b5cf6",
  "#06b6d4",
  "#e11d48",
  "#84cc16",
  "#a855f7",
  "#0ea5e9",
];

export default function RideAnalytics() {
  // ✅ State
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Overview
  const [overview, setOverview] = useState(null);
  const [weeklyData, setWeeklyData] = useState([]);

  // Monthly
  const [monthlyData, setMonthlyData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().getMonth() + 1
  );

  // City
  const [cityData, setCityData] = useState(null);
  const [cityType, setCityType] = useState("pickup");

  // Driver
  const [driverData, setDriverData] = useState([]);
  const [driverTotal, setDriverTotal] = useState(0);

  // Peak hours
  const [peakHoursData, setPeakHoursData] = useState(null);

  // 🗺️ Map state
  const [mapRides, setMapRides] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapDate, setMapDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [mapStatusFilter, setMapStatusFilter] = useState("all");
  const [selectedRide, setSelectedRide] = useState(null);
  const [selectedMarkerType, setSelectedMarkerType] = useState(null); // "pickup" or "drop"
  const [showRoutes, setShowRoutes] = useState(true);
  const [mapStatusCounts, setMapStatusCounts] = useState({});
  const mapRef = useRef(null);

  // ✅ Fetch overview
  const fetchOverview = useCallback(async () => {
    try {
      const [overviewRes, weeklyRes, peakRes] = await Promise.all([
        getRideOverview(),
        getWeeklyChartData(),
        getPeakHoursAnalysis(30),
      ]);
      if (overviewRes.success) setOverview(overviewRes.data);
      if (weeklyRes.success) setWeeklyData(weeklyRes.data);
      if (peakRes.success) setPeakHoursData(peakRes);
    } catch (err) {
      console.error("fetchOverview error:", err);
      toast.error("Failed to fetch overview data");
    }
  }, []);

  // ✅ Fetch monthly
  const fetchMonthlyData = useCallback(async () => {
    try {
      const res = await getMonthlyRideStats(selectedYear, selectedMonth);
      if (res.success) setMonthlyData(res);
    } catch (err) {
      console.error("fetchMonthlyData error:", err);
    }
  }, [selectedYear, selectedMonth]);

  // ✅ Fetch city
  const fetchCityData = useCallback(async () => {
    try {
      const res = await getCityWiseRides(null, null, cityType);
      if (res.success) setCityData(res);
    } catch (err) {
      console.error("fetchCityData error:", err);
    }
  }, [cityType]);

  // ✅ Fetch driver
  const fetchDriverData = useCallback(async () => {
    try {
      const res = await getDriverWiseRides(null, null, 1, 20);
      if (res.success) {
        setDriverData(res.drivers);
        setDriverTotal(res.total);
      }
    } catch (err) {
      console.error("fetchDriverData error:", err);
    }
  }, []);

  // 🗺️ Fetch map rides
  const fetchMapRides = useCallback(async () => {
    try {
      setMapLoading(true);
      const res = await getRidesForMap(mapDate, mapStatusFilter, null, 200);
      if (res.success) {
        setMapRides(res.rides || []);
        setMapStatusCounts(res.statusCounts || {});
      } else {
        toast.error(res.error || "Failed to fetch map rides");
      }
    } catch (err) {
      console.error("fetchMapRides error:", err);
    } finally {
      setMapLoading(false);
    }
  }, [mapDate, mapStatusFilter]);

  // ✅ Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchOverview(),
        fetchMonthlyData(),
        fetchCityData(),
        fetchDriverData(),
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchOverview, fetchMonthlyData, fetchCityData, fetchDriverData]);

  // Re-fetch on filter change
  useEffect(() => {
    if (!loading) fetchMonthlyData();
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    if (!loading) fetchCityData();
  }, [cityType]);

  // 🗺️ Fetch map data when tab switches to map or filters change
  useEffect(() => {
    if (activeTab === "map") {
      fetchMapRides();
    }
  }, [activeTab, mapDate, mapStatusFilter, fetchMapRides]);

  // ✅ Helpers
  const getChangeType = (change) => {
    if (change > 0) return "up";
    if (change < 0) return "down";
    return "neutral";
  };

  const formatNum = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num?.toLocaleString() || "0";
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // 🗺️ Fit map bounds to show all markers
  const fitMapBounds = () => {
    if (!mapRef.current || mapRides.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    mapRides.forEach((ride) => {
      if (ride.pickupLocation?.latitude && ride.pickupLocation?.longitude) {
        bounds.extend({
          lat: ride.pickupLocation.latitude,
          lng: ride.pickupLocation.longitude,
        });
      }
      if (ride.dropLocation?.latitude && ride.dropLocation?.longitude) {
        bounds.extend({
          lat: ride.dropLocation.latitude,
          lng: ride.dropLocation.longitude,
        });
      }
    });
    mapRef.current.fitBounds(bounds, { padding: 60 });
  };

  // 🗺️ Focus on specific ride
  const focusRide = (ride) => {
    if (!mapRef.current) return;
    setSelectedRide(ride);
    setSelectedMarkerType("pickup");
    const bounds = new window.google.maps.LatLngBounds();
    if (ride.pickupLocation?.latitude) {
      bounds.extend({
        lat: ride.pickupLocation.latitude,
        lng: ride.pickupLocation.longitude,
      });
    }
    if (ride.dropLocation?.latitude) {
      bounds.extend({
        lat: ride.dropLocation.latitude,
        lng: ride.dropLocation.longitude,
      });
    }
    mapRef.current.fitBounds(bounds, { padding: 80 });
  };

  if (loading) {
    return (
      <Loader />
    );
  }

  return (
    <div>
      <AccessControl
              allowedRoles={["SUPERADMIN", "ADMIN","HR", "NSM", "RSM", "ASM", "TSO"]}
              redirectTo="/404"
            />

      {/* ✅ Header */}
      <div
        className="page-header"
        style={{
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#1a1a1a" }}>
            📊 Ride Analytics
          </h1>
          <p style={{ fontSize: 13, color: "#666", marginTop: 4 }}>
            Track daily, monthly, city-wise & location-based ride statistics
          </p>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: "flex", gap: 6 }}>
          {[
            { key: "overview", label: "Overview", icon: "📊" },
            { key: "monthly", label: "Monthly", icon: "📆" },
            { key: "cities", label: "Cities", icon: "🏙️" },
            { key: "drivers", label: "Drivers", icon: "🛺" },
            { key: "map", label: "Map View", icon: "🗺️" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                border: "none",
                background:
                  activeTab === tab.key ? COLORS.primary : "#f1f5f9",
                color: activeTab === tab.key ? "#fff" : "#64748b",
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <span style={{ fontSize: 14 }}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ============ OVERVIEW TAB ============ */}
      {activeTab === "overview" && overview && (
        <>
          <div className="stat-grid section">
            <StatCard label="Today's Rides" value={overview.today.total} change={overview.today.changeText} changeType={getChangeType(overview.today.change)} iconBg="#E1F5EE" icon={<span style={{ fontSize: 18 }}>🛺</span>} />
            <StatCard label="This Week" value={overview.week.total} change={overview.week.changeText} changeType={getChangeType(overview.week.change)} iconBg="#E6F1FB" icon={<span style={{ fontSize: 18 }}>📅</span>} />
            <StatCard label="This Month" value={overview.month.total} change={overview.month.changeText} changeType={getChangeType(overview.month.change)} iconBg="#FAEEDA" icon={<span style={{ fontSize: 18 }}>📆</span>} />
            <StatCard label="All Time Rides" value={formatNum(overview.allTime.total)} change={`⭐ Avg Rating: ${overview.allTime.avgRating}`} changeType="neutral" iconBg="#EDE9FE" icon={<span style={{ fontSize: 18 }}>🏆</span>} />
          </div>
          <div className="grid-2 section">
            <div className="card">
              <div className="card-header"><span className="card-title">📈 Daily Rides (Last 7 Days)</span></div>
              <div className="chart-wrap">
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={weeklyData}>
                    <defs><linearGradient id="rideGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.2} /><stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} /></linearGradient></defs>
                    <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#888" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#888" }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12 }} />
                    <Area type="monotone" dataKey="total" stroke={COLORS.primary} strokeWidth={2} fill="url(#rideGrad)" />
                    <Area type="monotone" dataKey="completed" stroke={COLORS.success} strokeWidth={2} fill="none" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="card">
              <div className="card-header"><span className="card-title">📊 Ride Status Breakdown</span></div>
              <div className="chart-wrap" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={Object.entries(overview.statusBreakdown || {}).map(([status, count]) => ({ name: status.replace(/_/g, " "), value: count }))} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value" label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={false}>
                      {Object.keys(overview.statusBreakdown || {}).map((_, index) => (<Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          {peakHoursData && (
            <div className="card section">
              <div className="card-header">
                <span className="card-title">⏰ Peak Hours (Last 30 Days)</span>
                <div style={{ display: "flex", gap: 8 }}>
                  {peakHoursData.peakHours?.slice(0, 3).map((h, i) => (<span key={i} style={{ background: i === 0 ? "#fef3c7" : "#f1f5f9", padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, color: i === 0 ? "#b45309" : "#64748b" }}>🔥 {h.label}: {h.rides}</span>))}
                </div>
              </div>
              <div className="chart-wrap">
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={peakHoursData.hourlyData} barSize={20}>
                    <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#888" }} axisLine={false} tickLine={false} interval={1} />
                    <YAxis tick={{ fontSize: 10, fill: "#888" }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="rides" fill={COLORS.purple} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          <div className="section" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: 16, textAlign: "center" }}><div style={{ fontSize: 11, color: "#15803d", marginBottom: 4 }}>✅ Completed Today</div><div style={{ fontSize: 28, fontWeight: 700, color: "#166534" }}>{overview.today.completed}</div></div>
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: 16, textAlign: "center" }}><div style={{ fontSize: 11, color: "#dc2626", marginBottom: 4 }}>❌ Cancelled Today</div><div style={{ fontSize: 28, fontWeight: 700, color: "#b91c1c" }}>{overview.today.cancelled}</div></div>
            <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: 16, textAlign: "center" }}><div style={{ fontSize: 11, color: "#2563eb", marginBottom: 4 }}>💰 Today's Fare</div><div style={{ fontSize: 28, fontWeight: 700, color: "#1d4ed8" }}>₹{formatNum(overview.today.fare)}</div></div>
            <div style={{ background: "#faf5ff", border: "1px solid #e9d5ff", borderRadius: 10, padding: 16, textAlign: "center" }}><div style={{ fontSize: 11, color: "#7c3aed", marginBottom: 4 }}>💳 Cash Rides</div><div style={{ fontSize: 28, fontWeight: 700, color: "#6d28d9" }}>{overview.paymentModes?.cash || 0}</div></div>
          </div>
        </>
      )}

      {/* ============ MONTHLY TAB ============ */}
      {activeTab === "monthly" && (
        <div className="card section">
          <div className="card-header">
            <span className="card-title">📆 Monthly Statistics</span>
            <div style={{ display: "flex", gap: 8 }}>
              <select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 13 }}>
                {Array.from({ length: 12 }, (_, i) => (<option key={i + 1} value={i + 1}>{new Date(2000, i).toLocaleString("en-US", { month: "long" })}</option>))}
              </select>
              <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 13 }}>
                {[2024, 2025, 2026, 2027].map((y) => (<option key={y} value={y}>{y}</option>))}
              </select>
            </div>
          </div>
          {monthlyData && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 20 }}>
                <div style={{ background: "#f8fafc", padding: 16, borderRadius: 10, textAlign: "center" }}><div style={{ fontSize: 11, color: "#64748b" }}>Total Rides</div><div style={{ fontSize: 24, fontWeight: 700, color: "#0f172a" }}>{monthlyData.totals.total}</div></div>
                <div style={{ background: "#f0fdf4", padding: 16, borderRadius: 10, textAlign: "center" }}><div style={{ fontSize: 11, color: "#15803d" }}>Completed</div><div style={{ fontSize: 24, fontWeight: 700, color: "#166534" }}>{monthlyData.totals.completed}</div></div>
                <div style={{ background: "#fef2f2", padding: 16, borderRadius: 10, textAlign: "center" }}><div style={{ fontSize: 11, color: "#dc2626" }}>Cancelled</div><div style={{ fontSize: 24, fontWeight: 700, color: "#b91c1c" }}>{monthlyData.totals.cancelled}</div></div>
                <div style={{ background: "#eff6ff", padding: 16, borderRadius: 10, textAlign: "center" }}><div style={{ fontSize: 11, color: "#2563eb" }}>Total Fare</div><div style={{ fontSize: 24, fontWeight: 700, color: "#1d4ed8" }}>₹{formatNum(monthlyData.totals.fare)}</div></div>
                <div style={{ background: "#faf5ff", padding: 16, borderRadius: 10, textAlign: "center" }}><div style={{ fontSize: 11, color: "#7c3aed" }}>Avg/Day</div><div style={{ fontSize: 24, fontWeight: 700, color: "#6d28d9" }}>{monthlyData.totals.avgPerDay}</div></div>
              </div>
              <div className="chart-wrap">
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={monthlyData.dailyData} barSize={16}>
                    <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#888" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: "#888" }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="completed" stackId="a" fill={COLORS.success} name="Completed" />
                    <Bar dataKey="cancelled" stackId="a" fill={COLORS.danger} radius={[4, 4, 0, 0]} name="Cancelled" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ marginTop: 20 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>📅 Weekly Summary</h4>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
                  {monthlyData.weeklyData?.map((week) => (<div key={week.week} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, textAlign: "center" }}><div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Week {week.week} (Day {week.startDay}-{week.endDay})</div><div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>{week.total}</div><div style={{ fontSize: 11, color: "#22c55e" }}>✅ {week.completed} done</div></div>))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ============ CITIES TAB ============ */}
      {activeTab === "cities" && (
        <div className="card section">
          <div className="card-header">
            <span className="card-title">🏙️ City-wise Rides</span>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setCityType("pickup")} style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: cityType === "pickup" ? COLORS.primary : "#f1f5f9", color: cityType === "pickup" ? "#fff" : "#64748b", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>📍 Pickup</button>
              <button onClick={() => setCityType("drop")} style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: cityType === "drop" ? COLORS.primary : "#f1f5f9", color: cityType === "drop" ? "#fff" : "#64748b", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>🎯 Drop</button>
            </div>
          </div>
          {cityData && (
            <>
              <div style={{ display: "flex", gap: 20, marginBottom: 20, flexWrap: "wrap" }}>
                <div style={{ background: "#f8fafc", padding: "10px 20px", borderRadius: 8 }}><span style={{ fontSize: 12, color: "#64748b" }}>Total Cities: </span><strong>{cityData.totals.totalCities}</strong></div>
                <div style={{ background: "#f8fafc", padding: "10px 20px", borderRadius: 8 }}><span style={{ fontSize: 12, color: "#64748b" }}>Total Rides: </span><strong>{cityData.totals.totalRides}</strong></div>
                <div style={{ background: "#f8fafc", padding: "10px 20px", borderRadius: 8 }}><span style={{ fontSize: 12, color: "#64748b" }}>Total Fare: </span><strong>₹{formatNum(cityData.totals.totalFare)}</strong></div>
              </div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>#</th><th>City</th><th>Total Rides</th><th>Total Fare</th><th>Avg Fare</th><th>Share %</th></tr></thead>
                  <tbody>
                    {cityData.cities?.slice(0, 20).map((city, i) => (
                      <tr key={city.city}>
                        <td>{i + 1}</td>
                        <td style={{ fontWeight: 600 }}>📍 {city.city}</td>
                        <td>{city.rides}</td>
                        <td>₹{formatNum(city.totalFare)}</td>
                        <td>₹{city.avgFare}</td>
                        <td><div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 60, height: 6, background: "#e5e7eb", borderRadius: 3, overflow: "hidden" }}><div style={{ width: `${(city.rides / cityData.totals.totalRides) * 100}%`, height: "100%", background: COLORS.primary }} /></div><span style={{ fontSize: 11, color: "#64748b" }}>{((city.rides / cityData.totals.totalRides) * 100).toFixed(1)}%</span></div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ============ DRIVERS TAB ============ */}
      {activeTab === "drivers" && (
        <div className="card section">
          <div className="card-header"><span className="card-title">🛺 Driver-wise Rides</span><span style={{ fontSize: 12, color: "#64748b" }}>Total: {driverTotal} drivers</span></div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>#</th><th>Driver</th><th>Total</th><th>Completed</th><th>Cancelled</th><th>Completion %</th><th>Total Fare</th><th>Avg Rating</th><th>Status</th></tr></thead>
              <tbody>
                {driverData.map((driver, i) => (
                  <tr key={driver.driverId}>
                    <td>{i + 1}</td>
                    <td><div style={{ display: "flex", alignItems: "center", gap: 10 }}>{driver.photo ? (<img src={driver.photo} alt={driver.name} style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} />) : (<div style={{ width: 32, height: 32, borderRadius: "50%", background: "#e8e8e4", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>🛺</div>)}<div><div style={{ fontWeight: 500 }}>{driver.name}</div><div style={{ fontSize: 11, color: "#888" }}>{driver.vehicleNumber}</div></div></div></td>
                    <td>{driver.totalRides}</td>
                    <td style={{ color: COLORS.success, fontWeight: 600 }}>{driver.completedRides}</td>
                    <td style={{ color: COLORS.danger }}>{driver.cancelledRides}</td>
                    <td><div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 50, height: 6, background: "#e5e7eb", borderRadius: 3, overflow: "hidden" }}><div style={{ width: `${driver.completionRate}%`, height: "100%", background: driver.completionRate >= 80 ? COLORS.success : driver.completionRate >= 50 ? COLORS.warning : COLORS.danger }} /></div><span style={{ fontSize: 11 }}>{driver.completionRate}%</span></div></td>
                    <td>₹{formatNum(driver.totalFare)}</td>
                    <td><span style={{ color: "#f59e0b" }}>⭐ {driver.avgRating}</span></td>
                    <td><Badges status={driver.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ============ 🗺️ MAP VIEW TAB ============ */}
      {activeTab === "map" && (
        <div style={{ position: "relative" }}>
          {/* Map Controls */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              {/* Date picker */}
              <input
                type="date"
                value={mapDate}
                onChange={(e) => setMapDate(e.target.value)}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                  fontSize: 13,
                  background: "#fff",
                }}
              />

              {/* Status filter */}
              <select
                value={mapStatusFilter}
                onChange={(e) => setMapStatusFilter(e.target.value)}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                  fontSize: 13,
                  background: "#fff",
                }}
              >
                <option value="all">All Status</option>
                <option value="trip_completed">✅ Completed</option>
                <option value="cancelled">❌ Cancelled</option>
                <option value="accepted">🟡 Accepted</option>
                <option value="confirmed">🔵 Confirmed</option>
                <option value="requested">📨 Requested</option>
              </select>

              {/* Show routes toggle */}
              <button
                onClick={() => setShowRoutes(!showRoutes)}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "none",
                  background: showRoutes ? COLORS.primary : "#f1f5f9",
                  color: showRoutes ? "#fff" : "#64748b",
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                {showRoutes ? "🔗 Routes ON" : "🔗 Routes OFF"}
              </button>

              {/* Fit all button */}
              <button
                onClick={fitMapBounds}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                  background: "#fff",
                  color: "#475569",
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                📍 Fit All
              </button>
            </div>

            {/* Legend */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                fontSize: 12,
                color: "#64748b",
              }}
            >
              <span>
                🟢 Pickup
              </span>
              <span>
                🔴 Drop
              </span>
              <span>
                〰️ Route
              </span>
              <span style={{ fontWeight: 600, color: "#0f172a" }}>
                Total: {mapRides.length}
              </span>
            </div>
          </div>

          {/* Status count pills */}
          <div
            style={{
              display: "flex",
              gap: 8,
              marginBottom: 12,
              flexWrap: "wrap",
            }}
          >
            {Object.entries(mapStatusCounts).map(([status, count]) => (
              <span
                key={status}
                onClick={() =>
                  setMapStatusFilter(
                    mapStatusFilter === status ? "all" : status
                  )
                }
                style={{
                  padding: "5px 12px",
                  borderRadius: 20,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                  background:
                    mapStatusFilter === status
                      ? getStatusColor(status)
                      : `${getStatusColor(status)}15`,
                  color:
                    mapStatusFilter === status
                      ? "#fff"
                      : getStatusColor(status),
                  border: `1px solid ${getStatusColor(status)}40`,
                }}
              >
                {getStatusLabel(status)}: {count}
              </span>
            ))}
          </div>

          {/* Loading */}
          {mapLoading && (
            <div
              style={{
                position: "absolute",
                top: 100,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 999,
                background: "rgba(0,0,0,0.8)",
                color: "#fff",
                padding: "10px 24px",
                borderRadius: 12,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Loading rides on map...
            </div>
          )}

          <div style={{ display: "flex", gap: 16 }}>
            {/* ===== MAP ===== */}
            <div style={{ flex: 1 }}>
              <LoadScript googleMapsApiKey={GOOGLE_KEY}>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={defaultCenter}
                  zoom={12}
                  onLoad={(map) => {
                    mapRef.current = map;
                    setTimeout(fitMapBounds, 500);
                  }}
                  options={{
                    styles: [
                      { featureType: "poi", stylers: [{ visibility: "off" }] },
                    ],
                  }}
                >
                  {mapRides.map((ride, index) => (
                    <div key={ride.rideId || index}>
                      {/* 🟢 Pickup Marker */}
                      {ride.pickupLocation?.latitude && (
                        <Marker
                          position={{
                            lat: ride.pickupLocation.latitude,
                            lng: ride.pickupLocation.longitude,
                          }}
                          icon={{
                            path: window.google.maps.SymbolPath.CIRCLE,
                            scale: 8,
                            fillColor: "#22c55e",
                            fillOpacity: 1,
                            strokeColor: "#fff",
                            strokeWeight: 2,
                          }}
                          onClick={() => {
                            setSelectedRide(ride);
                            setSelectedMarkerType("pickup");
                          }}
                        />
                      )}

                      {/* 🔴 Drop Marker */}
                      {ride.dropLocation?.latitude && (
                        <Marker
                          position={{
                            lat: ride.dropLocation.latitude,
                            lng: ride.dropLocation.longitude,
                          }}
                          icon={{
                            path: window.google.maps.SymbolPath.CIRCLE,
                            scale: 8,
                            fillColor: "#ef4444",
                            fillOpacity: 1,
                            strokeColor: "#fff",
                            strokeWeight: 2,
                          }}
                          onClick={() => {
                            setSelectedRide(ride);
                            setSelectedMarkerType("drop");
                          }}
                        />
                      )}

                      {/* 〰️ Route Line */}
                      {showRoutes &&
                        ride.pickupLocation?.latitude &&
                        ride.dropLocation?.latitude && (
                          <Polyline
                            path={[
                              {
                                lat: ride.pickupLocation.latitude,
                                lng: ride.pickupLocation.longitude,
                              },
                              {
                                lat: ride.dropLocation.latitude,
                                lng: ride.dropLocation.longitude,
                              },
                            ]}
                            options={{
                              strokeColor:
                                ROUTE_COLORS[index % ROUTE_COLORS.length],
                              strokeOpacity: 0.6,
                              strokeWeight: 3,
                              geodesic: true,
                            }}
                          />
                        )}
                    </div>
                  ))}

                  {/* 🪧 InfoWindow */}
                  {selectedRide && selectedMarkerType && (
                    <InfoWindow
                      position={
                        selectedMarkerType === "pickup"
                          ? {
                              lat: selectedRide.pickupLocation?.latitude,
                              lng: selectedRide.pickupLocation?.longitude,
                            }
                          : {
                              lat: selectedRide.dropLocation?.latitude,
                              lng: selectedRide.dropLocation?.longitude,
                            }
                      }
                      onCloseClick={() => {
                        setSelectedRide(null);
                        setSelectedMarkerType(null);
                      }}
                    >
                      <div style={{ width: 260, fontSize: 12, lineHeight: 1.6 }}>
                        {/* Ride ID & Status */}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 8,
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 700,
                              fontSize: 14,
                              color: "#0f172a",
                            }}
                          >
                            {selectedRide.rideId}
                          </span>
                          <span
                            style={{
                              background: `${getStatusColor(selectedRide.status)}20`,
                              color: getStatusColor(selectedRide.status),
                              padding: "3px 10px",
                              borderRadius: 12,
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                          >
                            {getStatusLabel(selectedRide.status)}
                          </span>
                        </div>

                        {/* Pickup */}
                        <div
                          style={{
                            padding: "6px 8px",
                            background: "#f0fdf4",
                            borderRadius: 6,
                            marginBottom: 6,
                            borderLeft: "3px solid #22c55e",
                          }}
                        >
                          <div style={{ fontWeight: 600, color: "#15803d", fontSize: 10 }}>
                            🟢 PICKUP
                          </div>
                          <div style={{ color: "#475569", fontSize: 11 }}>
                            {selectedRide.pickupLocation?.address || "N/A"}
                          </div>
                        </div>

                        {/* Drop */}
                        <div
                          style={{
                            padding: "6px 8px",
                            background: "#fef2f2",
                            borderRadius: 6,
                            marginBottom: 8,
                            borderLeft: "3px solid #ef4444",
                          }}
                        >
                          <div style={{ fontWeight: 600, color: "#dc2626", fontSize: 10 }}>
                            🔴 DROP
                          </div>
                          <div style={{ color: "#475569", fontSize: 11 }}>
                            {selectedRide.dropLocation?.address || "N/A"}
                          </div>
                        </div>

                        {/* Details Grid */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 6,
                            background: "#f8fafc",
                            padding: 8,
                            borderRadius: 6,
                          }}
                        >
                          {selectedRide.fare > 0 && (
                            <div>
                              <span style={{ color: "#94a3b8", fontSize: 10 }}>Fare</span>
                              <div style={{ fontWeight: 700, color: "#059669" }}>
                                ₹{Math.round(selectedRide.fare)}
                              </div>
                            </div>
                          )}
                          {selectedRide.paymentMode && (
                            <div>
                              <span style={{ color: "#94a3b8", fontSize: 10 }}>Payment</span>
                              <div style={{ fontWeight: 600, color: "#475569", textTransform: "capitalize" }}>
                                {selectedRide.paymentMode === "cash" ? "💵" : "💳"}{" "}
                                {selectedRide.paymentMode}
                              </div>
                            </div>
                          )}
                          {selectedRide.driver?.name && (
                            <div>
                              <span style={{ color: "#94a3b8", fontSize: 10 }}>Driver</span>
                              <div style={{ fontWeight: 600, color: "#475569" }}>
                                🛺 {selectedRide.driver.name}
                              </div>
                            </div>
                          )}
                          {selectedRide.passenger?.name && (
                            <div>
                              <span style={{ color: "#94a3b8", fontSize: 10 }}>Passenger</span>
                              <div style={{ fontWeight: 600, color: "#475569" }}>
                                👤 {selectedRide.passenger.name}
                              </div>
                            </div>
                          )}
                          {selectedRide.rating > 0 && (
                            <div>
                              <span style={{ color: "#94a3b8", fontSize: 10 }}>Rating</span>
                              <div style={{ fontWeight: 600, color: "#f59e0b" }}>
                                ⭐ {selectedRide.rating}
                              </div>
                            </div>
                          )}
                          <div>
                            <span style={{ color: "#94a3b8", fontSize: 10 }}>Time</span>
                            <div style={{ fontWeight: 600, color: "#475569" }}>
                              🕐 {formatTime(selectedRide.createdAt)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </InfoWindow>
                  )}
                </GoogleMap>
              </LoadScript>
            </div>

            {/* ===== RIDE LIST SIDEBAR ===== */}
            <div
              style={{
                width: 300,
                background: "#fff",
                borderRadius: 12,
                border: "1px solid #e2e8f0",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                maxHeight: "calc(100vh - 220px)",
              }}
            >
              <div
                style={{
                  padding: "14px 16px",
                  borderBottom: "1px solid #e2e8f0",
                  background: "#f8fafc",
                }}
              >
                <h4 style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", margin: 0 }}>
                  Rides ({mapRides.length})
                </h4>
                <p style={{ fontSize: 11, color: "#94a3b8", margin: 0 }}>
                  Click to zoom on map
                </p>
              </div>
              <div style={{ flex: 1, overflowY: "auto" }}>
                {mapRides.length === 0 ? (
                  <div style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>🗺️</div>
                    <p style={{ fontSize: 13 }}>No rides on this date</p>
                  </div>
                ) : (
                  mapRides.map((ride, i) => (
                    <div
                      key={ride.rideId || i}
                      onClick={() => focusRide(ride)}
                      style={{
                        padding: "12px 14px",
                        borderBottom: "1px solid #f1f5f9",
                        cursor: "pointer",
                        transition: "all 0.15s",
                        background:
                          selectedRide?.rideId === ride.rideId
                            ? "#eef2ff"
                            : "transparent",
                        borderLeft:
                          selectedRide?.rideId === ride.rideId
                            ? `3px solid ${COLORS.primary}`
                            : "3px solid transparent",
                      }}
                    >
                      {/* Ride header */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 6,
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 700,
                            fontSize: 12,
                            color: "#0f172a",
                          }}
                        >
                          {ride.rideId}
                        </span>
                        <span
                          style={{
                            background: `${getStatusColor(ride.status)}15`,
                            color: getStatusColor(ride.status),
                            padding: "2px 8px",
                            borderRadius: 10,
                            fontSize: 10,
                            fontWeight: 700,
                          }}
                        >
                          {getStatusLabel(ride.status)}
                        </span>
                      </div>

                      {/* Pickup */}
                      <div
                        style={{
                          fontSize: 11,
                          color: "#475569",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 6,
                          marginBottom: 3,
                        }}
                      >
                        <span style={{ color: "#22c55e", fontSize: 10 }}>●</span>
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 240,
                          }}
                        >
                          {ride.pickupLocation?.address || "N/A"}
                        </span>
                      </div>

                      {/* Drop */}
                      <div
                        style={{
                          fontSize: 11,
                          color: "#475569",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 6,
                          marginBottom: 6,
                        }}
                      >
                        <span style={{ color: "#ef4444", fontSize: 10 }}>●</span>
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 240,
                          }}
                        >
                          {ride.dropLocation?.address || "N/A"}
                        </span>
                      </div>

                      {/* Bottom info */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          fontSize: 11,
                        }}
                      >
                        <div style={{ display: "flex", gap: 8, color: "#94a3b8" }}>
                          {ride.driver?.name && (
                            <span>🛺 {ride.driver.name}</span>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          {ride.fare > 0 && (
                            <span
                              style={{
                                fontWeight: 700,
                                color: "#059669",
                              }}
                            >
                              ₹{Math.round(ride.fare)}
                            </span>
                          )}
                          <span style={{ color: "#94a3b8" }}>
                            {formatTime(ride.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
